<script setup lang="ts">
import { ref } from 'vue'
import { Head } from '@inertiajs/vue3'
import DashboardLayout from '@/Layouts/DashboardLayout.vue'
import LibraryStats from '@/Components/Student/Dashboard/LibraryStats.vue'
import WeeklySchedule from '@/Components/Schedule/WeeklySchedule.vue'
import MyTutors from '@/Components/Student/Dashboard/MyTutors.vue'
import NotificationsList from '@/Components/Student/Dashboard/NotificationsList.vue'
import ContentLibrary from '@/Components/Student/Dashboard/ContentLibrary.vue'
import SubjectExplorer from '@/Components/Student/Dashboard/SubjectExplorer.vue'
import RecentActivity from '@/Components/Student/Dashboard/RecentActivity.vue'
import CalendarSessionModal from '@/Components/Schedule/CalendarSessionModal.vue'

interface LibraryStatsData {
  videos: number
  materials: number
  assignments: number
  saved: number
}

interface Lesson {
  id: number
  schedule_id?: number
  title: string
  counterpart_name: string
  subject: string
  subject_name?: string
  level_name?: string
  date: string
  time: string
  raw_time?: string
  type: 'one-on-one' | 'group'
  status?: string
  topic?: string | null
  note?: string | null
  duration_minutes?: number
  lesson_links?: {
    room_id: string
    teacher_link: string
    student_link: string
    generated_at: string
  } | null
}

interface Tutor {
  id: number
  name: string
  slug: string
  avatar_url: string | null
  subject: string
  tier_name: string
  status: string
}

interface Notification {
  id: number
  type: 'video' | 'lesson' | 'material' | 'general'
  title: string
  message: string
  time: string
  read: boolean
}

interface Content {
  id: number
  title: string
  thumbnail: string | null
  tutor_name: string
  tutor_avatar: string | null
  duration: string
  views: number
  subject: string
}

interface SubjectTutor {
  id: number
  name: string
  avatar_url: string | null
}

interface Subject {
  id: number
  name: string
  slug: string
  color: string
  icon: string
  tutor_count: number
  tutors: SubjectTutor[]
}

interface Activity {
  id: number
  type: 'video' | 'material' | 'assignment'
  title: string
  tutor_name: string
  subject: string
  progress: number
  duration?: string
  last_viewed?: string
}

defineProps<{
  libraryStats: LibraryStatsData
  lessons: Lesson[]
  tutors: Tutor[]
  notifications: Notification[]
  contents: Content[]
  subjects: Subject[]
  activities: Activity[]
}>()

// Session modal state
const showSessionModal = ref(false)
const selectedSession = ref<Lesson | null>(null)

function handleLessonClick(lesson: Lesson) {
  selectedSession.value = lesson
  showSessionModal.value = true
}
</script>

<template>
  <Head title="Student Dashboard" />

  <DashboardLayout>
    <div class="space-y-8">
      <!-- Library Stats -->
      <LibraryStats :stats="libraryStats" />

      <!-- Schedule (Full Width) -->
      <WeeklySchedule :lessons="lessons" @lesson-click="handleLessonClick" />

      <!-- My Tutors (Full Width) -->
      <MyTutors :tutors="tutors" />

      <!-- Notifications (Full Width) -->
      <NotificationsList :notifications="notifications" />

      <!-- Content Library -->
      <ContentLibrary :contents="contents" />

      <!-- Explore by Subject -->
      <SubjectExplorer :subjects="subjects" />
    </div>

    <!-- Session Detail Modal -->
    <CalendarSessionModal
      :show="showSessionModal"
      :session="selectedSession"
      :can-edit-note="false"
      @close="showSessionModal = false"
    />
  </DashboardLayout>
</template>
